import {
  CA_SURVIVAL_CARD_MIN_WIDTH,
  CA_SURVIVAL_CARD_MIN_HEIGHT,
  CA_TOP_ROW_GAP_PX,
  CA_VENN_OUTER_MIN_W,
} from '../store/cohortAnalyzerLayoutConstants';
import { cohortAnalyzerHiddenScrollbarStyles } from './cohortAnalyzerScrollbarStyles';

/** Chart summary view: Venn row, tabs, controls, category cards, table area. */
export const cohortAnalyzerSummaryStyles = (theme) => ({
  chartSummaryMain: {
    width: '100%',
    minWidth: 0,
    marginBottom: 1,
    marginTop: '16px',
    overflow: 'hidden',
  },
  chartSummaryHistogramFooter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: 14,
    paddingTop: 6,
    paddingBottom: 0,
    boxSizing: 'border-box',
  },
  vennSurvivalRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    alignContent: 'flex-start',
    gap: CA_TOP_ROW_GAP_PX,
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'hidden',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      flexWrap: 'nowrap',
      alignItems: 'stretch',
      gap: CA_TOP_ROW_GAP_PX,
    },
  },
  /**
   * Use content-based flex-basis so when survival wraps to its own row, the Venn (alone on
   * the first line) grows to the full row width; both peers still share a wide row when they fit.
   */
  vennColumn: {
    flex: `1 1 ${CA_VENN_OUTER_MIN_W}px`,
    minWidth: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    [theme.breakpoints.down('md')]: {
      flex: '1 1 auto',
      width: '100%',
    },
  },
  survivalBesideVennColumn: {
    flex: `1 1 ${CA_SURVIVAL_CARD_MIN_WIDTH}px`,
    minWidth: CA_SURVIVAL_CARD_MIN_WIDTH,
    minHeight: CA_SURVIVAL_CARD_MIN_HEIGHT,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    overflow: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      flex: '1 1 auto',
      width: '100%',
      minWidth: 0,
    },
  },
  rightSideAnalyzerInnerContainer: {
    display: 'flex',
    marginBottom: 0,
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    flex: '1 1 auto',
    alignItems: 'stretch',
  },
  rightSideAnalyzerHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    position: 'relative',
    width: '100%',
    alignSelf: 'center',
    alignItems: 'center',
    borderBottom: '2.5px solid #679AAA',
    maxHeight: '70px',
    '& h1': {
      fontFamily: 'Poppins',
      fontSize: '40px',
      fontWeight: 400,
      textAlign: 'left',
      color: '#0D3A3F',
      marginLeft: '30px',
      marginTop: '12px',
      marginBottom: '12px',
    },
  },
  readmeButton: {
    background: '#536D70',
    color: '#FFFFFF',
    border: '1px solid #2A4951',
    borderRadius: 4,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    height: 41,
    minWidth: 174,
    marginRight: 0,
    margin: 5,
    marginBottom: 20,
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },
  summaryViewShell: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    width: '100%',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    paddingTop: 14,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    overflowY: 'auto',
    ...cohortAnalyzerHiddenScrollbarStyles,
  },
  /** Row: chart/table tabs on the left, optional actions (e.g. README) on the far right. */
  summaryTabsBar: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: 30,
    paddingRight: 30,
    borderBottom: '0.5px solid #4E8191',
    marginBottom: 0,
  },
  summaryTabs: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    flex: '0 1 auto',
    minWidth: 0,
    boxSizing: 'border-box',
    gap: 20,
    marginBottom: 0,
  },
  summaryTabsBarEnd: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  summaryTab: {
    flex: '0 0 auto',
    width: '203px',
    border: '1px solid #BCBCBC',
    borderBottom: 'none',
    background: '#E8E8E8',
    color: '#1C2B33',
    fontFamily: 'Poppins',
    fontSize: 16,
    padding: '8px 16px',
    cursor: 'pointer',
    marginRight: 0,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: '6px solid #AFAFAF',
    textAlign: 'center',
  },
  summaryTabActive: {
    background: '#FFFFFF',
    color: '#000000',
    borderTop: '6px solid #3E7D8E',
    fontWeight: 500,
  },
  summaryTabPanel: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    alignSelf: 'stretch',
    boxSizing: 'border-box',
    backgroundColor: '#EFF5FB',
    padding: '14px 30px 24px 30px',
    marginTop: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'auto',
    ...cohortAnalyzerHiddenScrollbarStyles,
  },
  chartTopControlRow: {
    alignSelf: 'stretch',
    width: 'auto',
    marginLeft: -30,
    marginRight: -30,
    paddingLeft: 30,
    paddingRight: 30,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
    gap: 10,
    flexWrap: 'wrap',
    paddingBottom: 10,
    borderBottom: '1px solid #4E8191',
    marginTop: -5,
  },
  categoryPills: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  categoryPillButton: {
    border: '1px solid #95A4AA',
    borderRadius: 3,
    background: '#646464',
    fontFamily: 'Poppins',
    color: 'white',
    fontSize: 10.5,
    fontWeight: 500,
    lineHeight: '13px',
    padding: '2px 7px',
    letterSpacing: '0.01em',
    cursor: 'default',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
  chartActionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  addChartButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '0 16px',
    border: '1px solid #295665',
    borderRadius: 4,
    background: '#006A8F',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 12,
    height: 41,
    minWidth: 174,
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
  addChartButtonLabel: {
    textAlign: 'left',
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
  },
  addChartButtonIcon: {
    fontSize: 16,
    lineHeight: 1,
    position: 'relative',
    top: 1,
  },
  downloadAllButton: {
    border: '1px solid #2C4D59',
    borderRadius: 4,
    background: '#556469',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 11.5,
    height: 41,
    minWidth: 174,
    cursor: 'default',
    letterSpacing: '0.02em',
  },
  downloadAllDropdownRoot: {
    position: 'relative',
    flex: '0 0 auto',
  },
  downloadAllTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    width: '100%',
    minWidth: 174,
    height: 41,
    padding: '0 12px',
    boxSizing: 'border-box',
    border: '1px solid #1B4D55',
    borderRadius: 5,
    background: '#4A5560',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'left',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    textTransform: 'uppercase',
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
  /** Tight two-line label: default line-height stacks lines with too much gap after <br />. */
  downloadAllTriggerLabel: {
    display: 'block',
    lineHeight: 1.04,
  },
  downloadAllChevron: {
    fontSize: 10,
    lineHeight: 1,
    opacity: 0.95,
  },
  downloadAllMenu: {
    position: 'absolute',
    right: 0,
    top: '100%',
    marginTop: 0,
    marginBottom: 0,
    padding: 0,
    listStyle: 'none',
    minWidth: '100%',
    zIndex: 1300,
    border: '1px solid #18677A',
    borderTop: '1px solid #18677A',
    background: '#F4F8FA',
    boxSizing: 'border-box',
    '& li:last-of-type button': {
      borderBottom: 'none',
    },
  },
  downloadAllMenuItem: {
    display: 'block',
    width: '100%',
    margin: 0,
    padding: '10px 14px',
    border: 'none',
    borderBottom: '1px solid #18677A',
    background: '#F4F8FA',
    color: '#0D1F24',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'left',
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:hover': {
      background: '#9EB8C4',
    },
  },
  rightSideAnalyzerHeader2: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    minWidth: '30%',
    alignItems: 'flex-start',
    paddingBottom: 0,
    paddingTop: 0,
    backgroundColor: 'red',
    zIndex: 900,
    '& p': {
      fontFamily: 'Open Sans',
      fontSize: '15px',
      paddingBottom: 0,
      width: '100%',
      lineHeight: '20.8px',
      textAlign: 'left',
      color: 'black',
      margin: 1,
      marginTop: 0,
      marginLeft: '20px',
      [theme.breakpoints.down('lg')]: {
        width: '100%',
        marginLeft: 0,
      },
    },
  },
  alert: {
    position: 'absolute',
    top: 20,
    margin: 'auto',
  },
  catagoryCard: {
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 297,
    height: 194,
    marginLeft: 0,
    paddingBottom: 30,
    border: '1px solid #8B98AF',
    alignItems: 'center',
    alignContent: 'center',
    '& h3': {
      fontFamily: 'Poppins',
      fontSize: '18px',
      fontWeight: 400,
      textAlign: 'center',
      color: '#000',
    },
  },
  catagoryCardChildren: {
    width: '50%',
    minHeight: 75,
    alignSelf: 'center',
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    color: '#5D7B87',
    '& p': {
      top: 325,
      gap: 9,
      fontSize: 14,
      fontFamily: 'Poppins',
      fontWeight: 300,
      lineHeight: 2,
      margin: 0,
      cursor: 'pointer',
      '& input': {
        cursor: 'pointer',
      },
    },
  },
  rightSideTableContainer: {
    width: '100%',
    maxHeight: 540,
    overflowY: 'auto',
    ...cohortAnalyzerHiddenScrollbarStyles,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
});
